import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getOrganizationAvatarMutationKey, getOrganizationMutationKey } from '.';
import { organizationService } from '../../application/OrganizationService';
import { OrganizationActionType } from '../../context/organization/actions';
import { useOrganizationContext } from '../../context/organization/context';

import { Organization } from '../../domain/organization/Organization';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseRemoveOrganizationAvatarProps {
  organization: Organization;
}

type UseRemoveOrganizationAvatarResponseProps = ServiceResponse<Function, void>;

// Hooks act as 'Adapter' layer.
const useRemoveOrganizationAvatar = ({
  organization,
}: UseRemoveOrganizationAvatarProps): UseRemoveOrganizationAvatarResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const queryClient = useQueryClient();

  const { organizationDispatch } = useOrganizationContext();

  const { isLoading, isSuccess, isError, mutate } = useMutation<Organization, Error>(
    () => organizationService.deleteAvatar({ organization }),
    {
      onSuccess: (organization) => {
        organizationDispatch({ type: OrganizationActionType.FETCH_ORGANIZATION_SUCCESS, payload: { organization } });
        queryClient.invalidateQueries(getOrganizationMutationKey());
        queryClient.invalidateQueries(getOrganizationAvatarMutationKey());
      },
      onError: (err: any) => {
        console.info(`Remove Organization Avatar Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: mutate,
  };
};

export default useRemoveOrganizationAvatar;
